import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  Observable,
  catchError,
  finalize,
  throwError,
  from,
  switchMap,
  take,
} from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { environment } from '../../../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Auth } from '@angular/fire/auth';
import { AuthService } from '../authentication/auth.service';
import { QueryProgressService } from '../../shared/services/progress/query-progress.service';
import { Store } from '@ngrx/store';
import { selectDeviceState } from '../../store/selectors/app.selectors';

@Injectable()
export class HttpLoaderInterceptor implements HttpInterceptor {
  private loading = false;

  translocoService = inject(TranslocoService);
  _snackbar = inject(MatSnackBar);
  firebaseAuth = inject(Auth);
  authService = inject(AuthService);
  queryProgressService = inject(QueryProgressService);
  store = inject(Store);

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    this.showLoader();
    this.queryProgressService.setLoading(true);

    const apiUrl = environment.apiUrl;

    const excludedUrls = [
      `${apiUrl}/customer-drop/system/fetch-startup-data`,
      new RegExp(
        `^${apiUrl.replace(
          /[.*+?^${}()|[\]\\]/g,
          '\\$&'
        )}/customer-drop/nodes/.+/users$`
      ),
      /\/cdn\/assets\//,
    ];

    // Check if the request URL matches any of the excluded URLs
    const shouldAddToken = !excludedUrls.some((url) =>
      typeof url === 'string'
        ? request.url.includes(url)
        : url.test(request.url)
    );

    const isUsersEndpoint = new RegExp(
      `^${apiUrl.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&'
      )}/customer-drop/nodes/.+/users$`
    ).test(request.url);

    // If it's the users endpoint and not a GET request, force token requirement
    const needsTokenForUsersEndpoint =
      isUsersEndpoint && request.method !== 'GET';

    // TODO: Add deviceId as query param to all requests


    return from(this.getTokenAndCheckAuthentication()).pipe(
      switchMap(({ token, isAuthenticated }) =>
        this.store.select(selectDeviceState).pipe(
          take(1),
          switchMap((deviceState) => {
            if (
              token &&
              (shouldAddToken || needsTokenForUsersEndpoint) &&
              isAuthenticated
            ) {
              request = request.clone({
                setHeaders: {
                  Authorization: `Bearer ${token}`,
                },
              });
            }

            // Extract existing parameters
            let params = request.params;

            // Append deviceId from store if available
            const deviceId = deviceState?.device?.id;
            if (deviceId && deviceId !== '') {
              request.url.includes('deviceId') || (params = params.set('deviceId', deviceId));
            }

            const nodeId = deviceState?.device?.nodeIdNo;
            if (nodeId && nodeId !== '') {
              request.url.includes('nodeIdNo') || (params = params.set('nodeIdNo', nodeId));
            }

            // Optionally add language param in future
            // params = params.append('lan', this.translocoService.getActiveLang());

            // Clone the request with updated params
            request = request.clone({ params });

            const skipSnackbar = request.headers.has('X-Skip-Error-Snackbar');
            if (skipSnackbar) {
              request = request.clone({ headers: request.headers.delete('X-Skip-Error-Snackbar') });
            }

            return next.handle(request).pipe(
              catchError((error: HttpErrorResponse) => {
                const isUnauthorized = this.isUnauthorizedError(error, request);
                const networkError = error.status === 0;
                if (networkError) {
                  console.error('Possible network error', {
                    message: error.message
                  });
                }

                if (isUnauthorized) {
                  this._snackbar.open(this.translocoService.translate('snackbar.sessionTimedOut'), '✕', {
                    duration: 6000,
                    verticalPosition: 'bottom',
                  });

                  localStorage.removeItem('paxi-next-jwt');
                  this.authService.logout();
                } else if (!skipSnackbar) {
                  this._snackbar.open(error?.error?.message, '✕', {
                    duration: 6000, verticalPosition: 'bottom'
                  });
                }
                return throwError(() => error?.error);
              }),
              finalize(() => {
                this.queryProgressService.setLoading(false);
                this.hideLoader();
              })
            );
          })
        )
      )
    );
  }

  private async getTokenAndCheckAuthentication(): Promise<{
    token: string | null;
    isAuthenticated: boolean;
  }> {
    const token = localStorage.getItem('paxi-next-jwt');

    if (!token) {
      return { token: null, isAuthenticated: false };
    }

    const isAuthenticated = await this.authService.isAuthenticated();
    if (!isAuthenticated) {
      // Token might be expired, remove it from localStorage
      localStorage.removeItem('paxi-next-jwt');
      return { token: null, isAuthenticated: false };
    }

    return { token, isAuthenticated };
  }

  showLoader() {
    this.loading = true;
  }

  hideLoader() {
    this.loading = false;
  }

  private isUnauthorizedError(
    error: HttpErrorResponse,
    request: HttpRequest<any>
  ): boolean {
    const directStatus = error.status;
    const nestedStatus =
      (error.error as any)?.status ??
      (error.error as any)?.statusCode ??
      (error as any)?.statusCode;


    if (directStatus === 401 || nestedStatus === 401) {
      return true;
    }

    // In CORS/opaque failures Angular can surface status=0 even when backend sent 401.
    if (directStatus === 0) {
      const combinedMessage = `${error.message ?? ''} ${(error.error as any)?.message ?? ''} ${(error.error as any)?.error ?? ''}`.toLowerCase();
      const hasAuthHeader = request.headers.has('Authorization');
      const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;

      // Treat masked status=0 as unauthorized when request was authenticated and device is online.
      if (hasAuthHeader && isOnline) {
        return true;
      }
      return (
        combinedMessage.includes('401') ||
        combinedMessage.includes('unauthorized') ||
        combinedMessage.includes('unauthorised')
      );
    }

    return false;
  }
}
